import type { GetStaticPaths, GetStaticProps } from 'next'
import type { DehydratedState } from '@tanstack/react-query'
import { dehydrate, QueryClient } from '@tanstack/react-query'

import { createNonEmptyCategoriesQuery, createIntegrationsQuery } from '@segment/api/routes/catalog/queries'

import { createClient } from '@segment/api/algolia/client'

import { createCatalogAlphaIndex, createCatalogSearchIndex } from '@segment/contexts/catalogIndex'

import { createStaticSiteBackend } from '@segment/contexts/staticSiteBackend'

import type { AppExternalPageProps } from '@segment/next-shared/Providers'

import Catalog from './index'
import type { CatalogProps } from './index'

export const getStaticPaths: GetStaticPaths = async () => {
  const staticSiteBackend = createStaticSiteBackend()
  const algoliaClient = createClient()
  const catalogIndex = createCatalogSearchIndex(algoliaClient)
  const [, queryFn] = createNonEmptyCategoriesQuery({ catalogIndex, staticSiteBackend })
  const categories = await queryFn()

  return {
    paths: categories.map((category) => ({ params: { slug: category.slug } })),
    fallback: false,
  }
}

export const getStaticProps: GetStaticProps<AppExternalPageProps & CatalogProps> = async (ctx) => {
  const queryClient = new QueryClient()
  const algoliaClient = createClient()
  const catalogIndex = createCatalogAlphaIndex(algoliaClient)
  const catalogSearchIndex = createCatalogSearchIndex(algoliaClient)
  const staticSiteBackend = createStaticSiteBackend()
  let categoryId: string | null = null
  const query = ``
  const destination = true
  const source = true

  if (ctx?.params?.slug && !Array.isArray(ctx.params.slug)) {
    categoryId = ctx.params.slug
  }

  const categories = await queryClient.fetchQuery(
    ...createNonEmptyCategoriesQuery({ catalogIndex, staticSiteBackend }),
    { cacheTime: Infinity }
  )

  await queryClient.prefetchInfiniteQuery(
    ...createIntegrationsQuery(
      { catalogIndex, catalogSearchIndex },
      {
        query,
        category: categoryId,
        destination,
        source,
      }
    ),
    { staleTime: Infinity }
  )

  const dangerousDehydratedState = dehydrate(queryClient)

  const safeDehydratedState: DehydratedState = JSON.parse(JSON.stringify(dangerousDehydratedState))

  const props: AppExternalPageProps & CatalogProps = {
    dehydratedState: safeDehydratedState,
    categoryId,
    categories,
    filteringState: {
      query,
      destination,
      source,
    },
  }

  return {
    props,
  }
}

export default Catalog
